import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { take, catchError, throwError, switchMap, of } from 'rxjs';
import { Organization } from '../../models/organization.model';
import { BASE_ORG_TOKEN } from '../../tokens/selected-org.token';
import { UserPreferenceService } from '../user-preference/user-preference.service';
import { OrganizationService } from '../organization/organization.service';

@Injectable({
  providedIn: 'root',
})
export class OrgWorkflowService {
  private baseOrgSubject = inject(BASE_ORG_TOKEN);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly organizationService = inject(OrganizationService);

  baseOrg$ = this.baseOrgSubject.asObservable();

  private readonly router = inject(Router);

  private setBaseOrgIfRoot(currentOrg: Organization | null): void {
    if (currentOrg?.type === 'root' || currentOrg?.orgType === 'root') {
      this.baseOrgSubject.next(currentOrg);
      // Persist so the doorway marker survives a page refresh
      this.organizationService.saveBaseOrg(currentOrg);
    }
  }

  public switchOrg(org: Organization): void {
    this.userPreferenceService.selectedOrg
      .pipe(
        take(1),
        catchError((err) => {
          console.error('Error switching organization:', err);
          return throwError(() => err);
        })
      )
      .subscribe((currentOrg) => {
        this.setBaseOrgIfRoot(currentOrg);
        this.userPreferenceService.selectedOrg.next(org);
        // Also persist doorway target org so refresh restores it
        this.organizationService.saveSelectedOrganization(org);
        this.router.navigate(['/dashboard']);
      });
  }

  public switchToBaseOrg(): void {
    this.baseOrg$
      .pipe(
        take(1),
        catchError((err) => {
          console.error('Error switching to base organization:', err);
          return throwError(() => err);
        }),
        switchMap((baseOrg) => {
          if (!baseOrg) return of(null);

          // 1. Restore the root org context + persist it as selected
          this.userPreferenceService.selectedOrg.next(baseOrg);
          this.organizationService.saveSelectedOrganization(baseOrg);

          // 2. Clear the doorway marker from memory and localStorage
          this.baseOrgSubject.next(null);
          this.organizationService.clearBaseOrg();

          // 3. Fetch root org's branches, select the first one
          return this.organizationService.getBranches(baseOrg.id).pipe(
            take(1),
            catchError(() => of([])),
          );
        })
      )
      .subscribe((branches) => {
        if (branches && branches.length > 0) {
          // Select first branch of the root org
          this.organizationService.saveSelectedBranch(branches[0]);
        } else {
          // No branches — clear the branch selection
          this.userPreferenceService.setSelectedBranch(null);
        }

        // 4. Navigate to the Organizations portal
        this.router.navigate(['/organizations']);
      });
  }
}
